<template>
  <div>
    <div class="mb-4">
      <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Team - Performance Lead overzicht
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Overzicht van alle teams en hun toegewezen Performance Leads. Teams zonder toegewezen PL
        worden gemarkeerd.
      </p>
    </div>

    <!-- Loading state -->
    <div v-if="isLoadingTeamPL" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
    </div>

    <template v-else>
      <!-- Warning: teams without PL -->
      <div
        v-if="teamsWithoutPL.length > 0"
        class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6"
      >
        <h3 class="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
          Teams zonder Performance Lead
        </h3>
        <ul class="space-y-1">
          <li
            v-for="team in teamsWithoutPL"
            :key="team.simplicate_id"
            class="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400"
          >
            <ExclamationTriangleIcon class="h-4 w-4 flex-shrink-0" />
            <span>{{ team.name }} — geen toegewezen Performance Lead</span>
          </li>
        </ul>
      </div>

      <!-- All teams table -->
      <div
        class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      >
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Team
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Performance Lead(s)
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr
              v-for="team in teamPLData"
              :key="team.simplicate_id"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <td class="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                {{ team.name }}
              </td>
              <td class="px-4 py-3 text-sm">
                <template v-if="team.performance_leads.length > 0">
                  <span
                    v-for="(pl, idx) in team.performance_leads"
                    :key="pl.email"
                    class="text-gray-700 dark:text-gray-300"
                  >
                    {{ pl.name }}
                    <span class="text-gray-400 dark:text-gray-500">({{ pl.email }})</span>
                    <span v-if="idx < team.performance_leads.length - 1">, </span>
                  </span>
                </template>
                <span v-else class="text-gray-400 dark:text-gray-500 italic">— geen PL</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { adminService } from '@/services/adminService.js'

const { data: teamPLData, isLoading: isLoadingTeamPL } = useQuery({
  queryKey: ['team-pl-overview'],
  queryFn: () => adminService.getTeamPLOverview(),
  staleTime: 1000 * 60 * 5
})

const teamsWithoutPL = computed(() =>
  (teamPLData.value || []).filter(t => t.performance_leads.length === 0)
)

defineExpose({ teamsWithoutPL })
</script>
